/**
 * Phase 3 §7.22 License-readiness tab — operator-facing view of the
 * commercial-licensing posture across every wired source.
 *
 * What this surfaces:
 *   - One-screen scorecard: % verified, % paid-ready, NC count, unverified count
 *   - "Blocking paid launch" band — sources that filterByLicense('paid')
 *     excludes (every clearance ≠ 'cleared')
 *   - "Restricted explicit" band — verified NC sources (display-only OK)
 *   - "Verification queue" band — unverified entries, sorted by publisher
 *
 * The honesty rail: this surface NEVER renders a "verified" badge for a
 * source whose register entry has licenseVerified=false. The Phase 1
 * fail-closed default is visible here as the dominant grey block until
 * verification is complete.
 */

import { useMemo, useState } from 'react';
import {
  summariseLicenseReadiness, loadSourceLicenses, getSourceClearance,
  type PaidTierClearance,
} from '@biomax/plugin-surveillance-feed';
import { LicenseBadge, getClearanceMeta } from './license-primitives';

export function LicenseReadinessView() {
  const summary = useMemo(() => summariseLicenseReadiness(), []);
  const all = useMemo(() => loadSourceLicenses(), []);
  const [filter, setFilter] = useState<'blocking' | 'nc' | 'unverified' | 'cleared'>('blocking');

  const grouped = useMemo(() => {
    const buckets: Record<PaidTierClearance, Array<{ id: string; publisher: string; license: string }>> = {
      cleared: [], 'non-commercial': [], 'partnership-required': [],
      unverified: [], unregistered: [],
    };
    for (const [id, entry] of Object.entries(all)) {
      const c = getSourceClearance(id);
      buckets[c].push({ id, publisher: entry.publisher, license: entry.license });
    }
    for (const bucket of Object.values(buckets)) {
      bucket.sort((a, b) => a.publisher.localeCompare(b.publisher));
    }
    return buckets;
  }, [all]);

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8,
        marginBottom: 16,
      }}>
        <ReadinessKpi
          big n={summary.paidReadyPct} suffix="%" label="paid-tier ready"
          sub={`${summary.cleared} / ${summary.total} sources`}
          colorVar="var(--bm-state-live)"
        />
        <ReadinessKpi
          big n={summary.verifiedPct} suffix="%" label="verified"
          sub={`${summary.total - summary.unverified} / ${summary.total} read`}
          colorVar="var(--bm-state-stale)"
        />
        <ReadinessKpi
          n={summary.nonCommercial} label="non-commercial"
          sub="display-only — paid mode excludes"
          colorVar="var(--bm-state-degraded)"
        />
        <ReadinessKpi
          n={summary.partnershipRequired} label="partnership"
          sub="needs written agreement"
          colorVar="var(--bm-blocked)"
        />
        <ReadinessKpi
          n={summary.unverified} label="unverified"
          sub="fail-closed — paid mode excludes"
          colorVar="var(--bm-text-subtle)"
        />
      </div>

      <div style={{
        padding: '10px 14px', marginBottom: 12,
        fontSize: 12, lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
        color: 'var(--bm-text)',
      }}>
        <b style={{ color: 'var(--bm-state-degraded)' }}>{summary.blockingIds.length} sources</b> currently block paid-tier launch.
        The engine's fail-closed default (<code>filterByLicense('paid')</code>) excludes them automatically — so
        nothing ships under an unverified or non-commercial license without an explicit operator override.
        Address the blocking list to lift the restriction on each source.
      </div>

      <FilterTabs filter={filter} setFilter={setFilter} summary={summary} />

      {filter === 'blocking' && (
        <BlockingBand grouped={grouped} />
      )}
      {filter === 'nc' && (
        <Band title="Verified non-commercial · display-only" tone="var(--bm-state-degraded)">
          {grouped['non-commercial'].map(s => <SourceRow key={s.id} {...s} />)}
        </Band>
      )}
      {filter === 'unverified' && (
        <Band title="Unverified · needs terms-page read" tone="var(--bm-text-subtle)">
          {grouped.unverified.map(s => <SourceRow key={s.id} {...s} />)}
        </Band>
      )}
      {filter === 'cleared' && (
        <Band title="Cleared for paid output" tone="var(--bm-state-live)">
          {grouped.cleared.map(s => <SourceRow key={s.id} {...s} />)}
        </Band>
      )}

      <div style={{
        marginTop: 14, padding: '10px 14px',
        fontSize: 11.5, color: 'var(--bm-text-subtle)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        Source: <code>packages/plugin-surveillance-feed/data/source-licenses.json</code>.
        Each verified entry has been WebFetched + classified against SPDX.
        Unverified entries have <code>licenseVerified: false</code> and require a
        terms-page read before they can be admitted to paid output. The
        runtime enforcement layer (<code>licenses.ts</code>) reads the register
        on import; updates take effect on next reload.
      </div>
    </div>
  );
}

function BlockingBand({ grouped }: { grouped: Record<PaidTierClearance, Array<{ id: string; publisher: string; license: string }>> }) {
  // "Blocking" = anything not cleared. Show by clearance category so operators
  // know which lever to pull (verify, secure-partnership, or accept-NC-display-only).
  return (
    <>
      <Band title={`Non-commercial · ${grouped['non-commercial'].length}`} tone="var(--bm-state-degraded)">
        {grouped['non-commercial'].length === 0
          ? <Empty>None.</Empty>
          : grouped['non-commercial'].map(s => <SourceRow key={s.id} {...s} />)}
      </Band>
      <Band title={`Partnership-required · ${grouped['partnership-required'].length}`} tone="var(--bm-blocked)">
        {grouped['partnership-required'].length === 0
          ? <Empty>None.</Empty>
          : grouped['partnership-required'].map(s => <SourceRow key={s.id} {...s} />)}
      </Band>
      <Band title={`Unverified · ${grouped.unverified.length}`} tone="var(--bm-text-subtle)">
        {grouped.unverified.length === 0
          ? <Empty>None.</Empty>
          : grouped.unverified.map(s => <SourceRow key={s.id} {...s} />)}
      </Band>
    </>
  );
}

function FilterTabs({ filter, setFilter, summary }: {
  filter: 'blocking' | 'nc' | 'unverified' | 'cleared';
  setFilter: (f: 'blocking' | 'nc' | 'unverified' | 'cleared') => void;
  summary: ReturnType<typeof summariseLicenseReadiness>;
}) {
  const tabs: ReadonlyArray<{ k: typeof filter; label: string; count: number; tone: string }> = [
    { k: 'blocking',   label: 'Blocking paid launch', count: summary.blockingIds.length, tone: 'var(--bm-state-degraded)' },
    { k: 'nc',         label: 'Non-commercial',       count: summary.nonCommercial,      tone: 'var(--bm-state-degraded)' },
    { k: 'unverified', label: 'Unverified',           count: summary.unverified,         tone: 'var(--bm-text-subtle)' },
    { k: 'cleared',    label: 'Cleared',              count: summary.cleared,            tone: 'var(--bm-state-live)' },
  ];
  return (
    <div style={{
      display: 'flex', gap: 6, marginBottom: 10,
    }}>
      {tabs.map(t => {
        const isOn = filter === t.k;
        return (
          <button
            key={t.k}
            type="button"
            onClick={() => setFilter(t.k)}
            style={{
              padding: '5px 11px',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
              background: isOn ? 'var(--bm-bg)' : 'transparent',
              color: isOn ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              border: `1px solid ${isOn ? t.tone : 'var(--bm-border)'}`,
              borderRadius: 4, cursor: 'pointer',
            }}
          >
            {t.label}
            <span style={{
              padding: '0 6px', borderRadius: 8,
              background: isOn ? t.tone : 'var(--bm-bg-subtle)',
              color: isOn ? 'var(--bm-bg)' : 'var(--bm-text-subtle)',
              fontSize: 10, fontWeight: 700,
            }}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function ReadinessKpi({ big = false, n, suffix = '', label, sub, colorVar }: {
  big?: boolean; n: number; suffix?: string; label: string; sub: string; colorVar: string;
}) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: big ? 22 : 17, fontWeight: 700,
        color: colorVar,
      }}>{n.toLocaleString()}{suffix}</div>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
        letterSpacing: '.4px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)', marginTop: 2,
      }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--bm-text-muted)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function Band({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return (
    <section style={{
      marginBottom: 12,
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)',
      overflow: 'hidden',
    }}>
      <header style={{
        padding: '7px 12px',
        borderBottom: '1px solid var(--bm-border)',
        borderLeft: `3px solid ${tone}`,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700,
        letterSpacing: '.4px', color: 'var(--bm-text)',
      }}>{title}</header>
      <div>{children}</div>
    </section>
  );
}

function SourceRow({ id, publisher, license }: { id: string; publisher: string; license: string }) {
  const clearance = getSourceClearance(id);
  const meta = getClearanceMeta(clearance);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1.6fr) auto',
      gap: 10, alignItems: 'center',
      padding: '7px 12px',
      borderBottom: '1px solid var(--bm-border)',
      borderLeft: `3px solid ${meta.color}`,
      fontSize: 12,
    }}>
      <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text)' }}>{id}</span>
      <span style={{ color: 'var(--bm-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{publisher}</span>
      <span style={{ color: 'var(--bm-text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{license}</span>
      <LicenseBadge sourceId={id} small />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '10px 12px',
      fontSize: 12, color: 'var(--bm-text-subtle)', fontStyle: 'italic' as const,
    }}>{children}</div>
  );
}
